import { api } from "./client";
import type { Money } from "./types";

/*
 * Multi-currency and price books (server/currency*.js, server/pricebooks.js). Every amount the server sends is a decimal string in
 * the currency named beside it (INR when none is named); nothing here does money arithmetic. Docs: docs/implemented-currency.md.
 */

const get = <T,>(path: string, query?: Record<string, string | null | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v) qs.set(k, v);
  const s = qs.toString();
  return api<T>("GET", s ? `${path}?${s}` : path);
};
const post = <T,>(path: string, body: unknown = {}) => api<T>("POST", path, body);
const patch = <T,>(path: string, body: unknown) => api<T>("PATCH", path, body);
const del = (path: string) => api<void>("DELETE", path);

export type NumberFormat = "comma_dot" | "indian" | "dot_comma" | "space_comma" | "plain";

export type Currency = {
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  format: NumberFormat;
  format_label: string;
  is_base: boolean;
  active: boolean;
  latest_rate: { date: string | null; rate: string } | null;
  updated_at: string;
  updated_by_name: string | null;
};
export type CurrencyIn = { code?: string; name?: string; symbol?: string; decimals?: number; format?: NumberFormat; active?: boolean; rate?: string; rate_date?: string };
export type Rate = { id: string; currency_code: string; date: string; rate: string; note: string | null; created_at: string; created_by_name: string | null };

export type TreatmentMode = "normal" | "zero" | "igst";
export type Treatment = {
  code: string;
  label: string;
  mode: TreatmentMode;
  place_code: string | null;
  declaration: string | null;
  lut_number: string | null;
  lut_valid_from: string | null;
  lut_valid_to: string | null;
  note: string | null;
  active: boolean;
  is_system: boolean;
};
export type TreatmentIn = Partial<Omit<Treatment, "is_system">>;

export type PartySettings = { currency_code: string; tax_treatment: string | null; pricebook_id: string | null };

export type CurrencyMeta = {
  base_currency: string;
  currencies: Currency[];
  catalog: { code: string; name: string; symbol: string; decimals: number }[];
  formats: { value: NumberFormat; label: string }[];
  treatments: Treatment[];
  parties: { customer: Record<string, PartySettings>; vendor: Record<string, PartySettings> };
};

export type PriceBook = {
  id: string;
  name: string;
  description: string | null;
  scheme: "percent" | "per_item";
  direction: "markup" | "markdown";
  percent: string;
  rounding: "none" | "whole";
  currency_code: string | null;
  status: "active" | "inactive";
  item_count: number;
  customer_count: number;
  items?: { item_id: string; name: string; sku: string | null; item_status: string | null; catalog_rate: Money | null; rate: Money }[];
};
export type PriceBookIn = Partial<Omit<PriceBook, "id" | "item_count" | "customer_count" | "items">> & { items?: { item_id: string; rate: string }[] };

export type AdjustmentRow = { kind: "invoice" | "bill" | "bank" | "customer_advance" | "vendor_advance"; id: string; number: string; party: string; date: string; document_rate: string | null; lines_without_rate?: number; open_amount: Money; open_amount_text: string; carrying: Money; revalued: Money; difference: Money };
export type AdjustmentPreview = { date: string; currency_code: string; rate: string; rate_typed: boolean; rate_on_file: { date: string | null; rate: string } | null; receivable_change: Money; payable_change: Money; gain: Money; rows: AdjustmentRow[] };
export type Adjustment = {
  id: string;
  date: string;
  currency_code: string;
  rate: string;
  receivable_change: Money;
  payable_change: Money;
  gain: Money;
  notes: string | null;
  status: "posted" | "undone";
  undone_at: string | null;
  journal: { id: string; number: string; date: string };
  reversal: { id: string; number: string; date: string };
  created_at: string;
  created_by_name: string | null;
  rows?: AdjustmentRow[];
};

export type FxTransaction = {
  kind: "invoice" | "credit_note" | "payment_received" | "bill" | "payment_made" | "expense" | "advance_received" | "advance_applied" | "advance_refunded"; // the advance kinds: wave 9 (server/fx9.js)
  advance_number?: string;
  id: string;
  date: string;
  number: string | null;
  party: string | null;
  status: string;
  currency_code: string;
  rate: string;
  amount: Money;
  amount_text: string;
  bcy_amount: Money;
  exchange_gain: Money | null;
  balance?: Money;
};

export const currency = {
  meta: () => get<CurrencyMeta>("/admin/currency/meta"),
  rate: (code: string, date: string) => get<{ currency_code: string; date: string; rate: string | null; rate_date: string | null }>("/admin/currency/rate", { currency: code, date }),
  /** customer_id: a zero-rated (export) customer gets the catalog rate less its GST (Billing › GST: export pricing). */
  price: (q: { item_id: string; pricebook_id?: string | null; currency?: string | null; rate?: string | null; customer_id?: string | null }) =>
    get<{ item_id: string; currency_code: string; rate: Money; source: string; pricebook_id: string | null; gst_removed?: boolean }>("/admin/currency/price", q),
  list: () => get<Currency[]>("/admin/currency/currencies"),
  create: (b: CurrencyIn) => post<Currency>("/admin/currency/currencies", b),
  update: (code: string, b: CurrencyIn) => patch<Currency>(`/admin/currency/currencies/${code}`, b),
  remove: (code: string) => del(`/admin/currency/currencies/${code}`),
  rates: (code: string) => get<Rate[]>(`/admin/currency/currencies/${code}/rates`),
  setRate: (code: string, b: { date: string; rate: string; note?: string | null }) => post<{ currency_code: string; date: string; rate: string }>(`/admin/currency/currencies/${code}/rates`, b),
  removeRate: (code: string, id: string) => del(`/admin/currency/currencies/${code}/rates/${id}`),
  treatments: () => get<Treatment[]>("/admin/currency/treatments"),
  saveTreatment: (code: string | null, b: TreatmentIn) => (code ? patch<Treatment>(`/admin/currency/treatments/${code}`, b) : post<Treatment>("/admin/currency/treatments", b)),
  books: (status?: string) => get<PriceBook[]>("/admin/currency/pricebooks", { status }),
  book: (id: string) => get<PriceBook>(`/admin/currency/pricebooks/${id}`),
  createBook: (b: PriceBookIn) => post<PriceBook>("/admin/currency/pricebooks", b),
  updateBook: (id: string, b: PriceBookIn) => patch<PriceBook>(`/admin/currency/pricebooks/${id}`, b),
  removeBook: (id: string) => del(`/admin/currency/pricebooks/${id}`),
  adjustments: () => get<{ adjustments: Adjustment[]; currencies: Currency[] }>("/admin/currency/adjustments"),
  adjustment: (id: string) => get<Adjustment>(`/admin/currency/adjustments/${id}`),
  previewAdjustment: (b: { date: string; currency_code: string; rate?: string | null }) => post<AdjustmentPreview>("/admin/currency/adjustments/preview", b),
  createAdjustment: (b: { date: string; currency_code: string; rate?: string | null; notes?: string | null }) => post<Adjustment>("/admin/currency/adjustments", b),
  undoAdjustment: (id: string) => post<Adjustment>(`/admin/currency/adjustments/${id}/undo`),
  transactions: (q: { from?: string; to?: string; currency?: string; side?: string }) =>
    get<{ from: string; to: string; rows: FxTransaction[]; totals: { currency_code: string; count: number; exchange_gain: Money }[] }>("/admin/currency/transactions", q),
};

/** The currency fields a foreign document, payment or row carries (absent on INR ones). */
export type CurrencyOf = { currency_code?: string; currency_symbol?: string; currency_decimals?: number; currency_format?: NumberFormat; exchange_rate?: string };
